import { PersonClient, PersonDto, PersonListDto } from "@app/api2/api";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Header, Icon, Message, Segment, Form as SForm, FormGroup as SFormGroup } from "@/components/bp/bp-ui";
import { buildUrl, AppRoutes } from "../../../AppRoutes";
import BPSimpleTable from "@common/BPSimpleTable";
import SemanticFormikInput from "@common/Forms/SemanticFormikInput";
import SemanticFormikSelect from "@common/Forms/SemanticFormikSelect";
import { isNullOrWhitespace, keyOfCheckerNested } from "@common/Utils";
import { SectionWithInfo } from "../components/SectionWithInfo";
import { BPSection } from "@app/components/React/Common/BPSection";
import { EnrollmentFormSection } from "../components/EnrollmentFormSection";
import { useCountryOptions, useEnrollmentPurposeOptions, useSexOptions } from "../../Hooks/LookupOptions";
import SemanticFormikCheckbox from "@app/components/React/Common/Forms/SemanticFormikCheckbox";

const fn = keyOfCheckerNested<PersonDto>();
const client = new PersonClient();


export const BiographicalTab = ({ }) => {
    const { values, submitForm } = useFormikContext<PersonDto>();
    const [searchResults, setSearchResults] = useState<PersonListDto[]>([]);
    const [inSearchMode, setInSearchMode] = useState(false);
    const enrollmentPurposeOptions = useEnrollmentPurposeOptions({ defaultValue: values.enrollmentPurposeId });
    const sexOptions = useSexOptions({ defaultValue: values.sexId });
    const countryOptions = useCountryOptions({ defaultValue: values.nationalityId });

    const isNewRecord = !values.id;

    return (
        <SectionWithInfo title="Biographical Information" info="This section collects the core biographical and identifying details for the individual being enrolled. Please ensure the information is accurate, as it forms the foundation of the record.">
            <EnrollmentFormSection sectionKey="enrollment_details" header="Enrollment Details" >
                {({ editMode }) => (
                    <>
                        <SemanticFormikSelect disabled={!editMode} name={fn("enrollmentPurposeId")} 
                            placeholder="Select Enrollment Purpose"
                            {...enrollmentPurposeOptions.getDropdownProps()} />
                        {/* <SemanticFormikCheckbox name={fn("isEnrollmentCompleted")} label="Mark Enrollment as Completed" /> */}
                    </>
                )}
            </EnrollmentFormSection>
            <EnrollmentFormSection sectionKey="personal_details" header="Personal Details" >
                {({ editMode }) => (<>
                    <SFormGroup widths="equal">
                        <SemanticFormikInput disabled={!editMode} name={fn("lastName")} maxLength={80} />
                        <SemanticFormikInput disabled={!editMode} name={fn("secondLastName")} maxLength={80} />
                    </SFormGroup>
                    <SFormGroup widths="equal">
                        <SemanticFormikInput disabled={!editMode} name={fn("firstName")} maxLength={80} />
                        <SemanticFormikInput disabled={!editMode} name={fn("middleName")} maxLength={80} />
                    </SFormGroup>
                    <SemanticFormikInput disabled={!editMode} name={fn("dob")} type="date" />
                </>)}
            </EnrollmentFormSection>
            {isNewRecord &&
                <EnrollmentFormSection sectionKey="record_search" basic header="Record Search & Verification" info="Enter Last Name and Date of Birth, then search to check for existing records before proceeding.">
                    <Button
                        type="button"
                        color="blue"
                        icon
                        disabled={isNullOrWhitespace(values.lastName) || !values.dob}
                        labelPosition="left"
                        onClick={async () => {
                            const result = await client.list(10, 1, 'lastName', 'asc', values.lastName, values.dob ? new Date(values.dob) : undefined);
                            setSearchResults(result.items);
                            setInSearchMode(true)
                        }}
                    >
                        <Icon name="search" />
                        Search for Record
                    </Button>
                    {/* <Divider hidden /> */}
                    <div style={{ marginTop: '1em' }}>
                        {inSearchMode && searchResults.length > 0 && <BPSimpleTable
                            data={searchResults}
                            columns={[
                                { accessor: 'lastName', header: 'Last Name', Cell: cell => <Link to={buildUrl(AppRoutes.ProfilesMetadataEdit, { metaId: cell.id })}>{cell.lastName}</Link> },
                                { accessor: 'firstName', header: 'First Name' },
                                { accessor: 'dob', header: 'Date of Birth' },
                            ]}
                        />}
                        {inSearchMode && searchResults.length == 0 && <Message content="No matching records found." />}
                    </div>
                </EnrollmentFormSection>
            }
            <EnrollmentFormSection sectionKey="additional_biographical_details" header="Additional Biographical Details" >
                {({ editMode }) => (
                    <SFormGroup widths="equal">
                        <SemanticFormikSelect
                            disabled={!editMode}
                            name={fn("sexId")}
                            placeholder="Select Sex"
                            required
                            {...sexOptions.getDropdownProps()}
                        />
                        <SemanticFormikSelect
                            disabled={!editMode}
                            name={fn("nationalityId")}
                            placeholder="Select Nationality"
                            required
                            {...countryOptions.getDropdownProps()}
                        />
                    </SFormGroup>
                )}
            </EnrollmentFormSection>
            <EnrollmentFormSection sectionKey="agency_record_identifiers" header="Agency & Record Identifiers"  >
                {({ editMode }) => (<>
                    <SFormGroup widths="equal">
                        <SemanticFormikInput disabled={!editMode} name={fn("agencyOri")} label="Agency ORI" maxLength={9} />
                        <SemanticFormikInput disabled={!editMode} name={fn("agencyCaseNumber")} label="Agency Case Number (OCA/OCE)" maxLength={12} />
                    </SFormGroup>
                    <SFormGroup widths="equal">
                        <SemanticFormikInput disabled={!editMode} name={fn("fbiNumber")} label="FBI Number" maxLength={20} />
                        <SemanticFormikInput disabled={!editMode} name={fn("sidNumber")} label="SID Number (State ID)" maxLength={20} />
                    </SFormGroup>
                </>
                )}
            </EnrollmentFormSection>
        </SectionWithInfo>
    );
};